import { Link } from "react-router-dom";
import { Shield, Mail, Instagram, Youtube } from "lucide-react";

const XIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const ThreadsIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.01v-.017c.03-3.579.879-6.43 2.525-8.482C5.845 1.205 8.6.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.04.569c-1.104-3.96-3.898-5.984-8.304-6.015-2.91.022-5.11.936-6.54 2.717C4.307 6.504 3.616 8.914 3.589 12c.027 3.086.718 5.496 2.057 7.164 1.43 1.781 3.631 2.695 6.54 2.717 2.623-.02 4.358-.631 5.8-2.045 1.647-1.613 1.618-3.593 1.09-4.798-.31-.71-.873-1.3-1.634-1.75-.192 1.352-.622 2.446-1.284 3.272-.886 1.102-2.14 1.704-3.73 1.79-1.202.065-2.36-.218-3.259-.801-1.063-.689-1.685-1.74-1.752-2.964-.065-1.19.408-2.285 1.33-3.082.88-.76 2.119-1.207 3.583-1.291a13.853 13.853 0 0 1 3.02.142c-.126-.742-.375-1.332-.74-1.757-.502-.586-1.28-.883-2.31-.89h-.029c-.825 0-1.945.227-2.661 1.29l-1.71-1.149c.96-1.43 2.52-2.217 4.37-2.217h.043c3.094.02 4.937 1.92 5.12 5.226.105.044.211.092.314.142 1.469.69 2.544 1.737 3.107 3.026.786 1.797.857 4.726-1.527 7.058-1.821 1.781-4.034 2.585-7.181 2.607Zm1.062-11.165c-.252 0-.504.008-.752.025-1.834.103-2.974.94-2.91 2.13.067 1.247 1.443 1.828 2.769 1.756 1.221-.067 2.815-.539 3.082-3.681a10.97 10.97 0 0 0-2.189-.23z" />
  </svg>
);

export function Footer() {
  return (
    <footer className="border-t bg-muted/30">
      <div className="container py-12">
        <div className="grid gap-8 md:grid-cols-4">

          {/* Brand + business info */}
          <div className="md:col-span-2">
            <Link to="/" className="flex items-center gap-2 mb-4">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg gradient-hero">
                <Shield className="h-4 w-4 text-primary-foreground" />
              </div>
              <span className="text-base font-semibold text-foreground">Transparency Badge</span>
            </Link>
            <p className="text-sm text-muted-foreground max-w-md mb-4">
              A website safety and trust verification platform. Free safety checker for visitors. Verified Badge Program for website owners who want to build trust.
            </p>
            <div className="space-y-1.5 text-sm text-muted-foreground">
              <p>🌍 Hyderabad, India</p>
              <a href="mailto:hello@transparencybadge.com" className="flex items-center gap-1.5 hover:text-foreground transition-colors">
                <Mail className="h-3.5 w-3.5" />
                hello@transparencybadge.com
              </a>
            </div>
            <p className="text-xs font-medium text-muted-foreground mt-4 mb-2">Follow us on social media</p>
            <div className="flex gap-3">
              <a href="https://www.instagram.com/transparencybadge/" target="_blank" rel="noopener noreferrer" aria-label="Transparency Badge on Instagram" className="text-muted-foreground hover:text-foreground transition-colors">
                <Instagram className="h-4 w-4" aria-hidden="true" />
              </a>
              <a href="https://x.com/trustbeforecliq" target="_blank" rel="noopener noreferrer" aria-label="Transparency Badge on X" className="text-muted-foreground hover:text-foreground transition-colors">
                <XIcon className="h-4 w-4" />
              </a>
              <a href="https://www.threads.net/@transparencybadge" target="_blank" rel="noopener noreferrer" aria-label="Transparency Badge on Threads" className="text-muted-foreground hover:text-foreground transition-colors">
                <ThreadsIcon className="h-4 w-4" />
              </a>
              <a href="https://www.youtube.com/@transparencybadge" target="_blank" rel="noopener noreferrer" aria-label="Transparency Badge on YouTube" className="text-muted-foreground hover:text-foreground transition-colors">
                <Youtube className="h-4 w-4" aria-hidden="true" />
              </a>
            </div>
          </div>

          {/* Learn More */}
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-4">Product</h3>
            <ul className="space-y-2">
              <li><Link to="/how-it-works" className="text-sm text-muted-foreground hover:text-foreground transition-colors">How It Works</Link></li>
              <li><Link to="/data-sources" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Data Sources</Link></li>
              <li><Link to="/badge-meaning" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Badge Meaning</Link></li>
              <li><Link to="/verification-process" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Verification Process</Link></li>
              <li><Link to="/pricing" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Pricing</Link></li>
              <li><Link to="/get-verified" className="text-sm text-primary font-medium hover:underline transition-colors">Get Verified →</Link></li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-4">Legal</h3>
            <ul className="space-y-2">
              <li><Link to="/privacy" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Privacy Policy</Link></li>
              <li><Link to="/terms" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Terms of Service</Link></li>
              <li><Link to="/refund-policy" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Refund Policy</Link></li>
              <li><Link to="/disclaimer" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Disclaimer</Link></li>
              <li><Link to="/affiliate-disclosure" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Affiliate Disclosure</Link></li>
              <li><Link to="/transparency" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Transparency</Link></li>
              <li><Link to="/about" className="text-sm text-muted-foreground hover:text-foreground transition-colors">About Us</Link></li>
              <li><Link to="/contact" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Contact</Link></li>
            </ul>
          </div>

        </div>

        <div className="border-t mt-8 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-muted-foreground">
            © {new Date().getFullYear()} Universal Website Transparency Badge. All rights reserved.
          </p>
          <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
            <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-foreground transition-colors">Terms</Link>
            <Link to="/refund-policy" className="hover:text-foreground transition-colors">Refunds</Link>
            <Link to="/disclaimer" className="hover:text-foreground transition-colors">Disclaimer</Link>
            <Link to="/affiliate-disclosure" className="hover:text-foreground transition-colors">Affiliate Disclosure</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
